"use client";

import React, { useState, useMemo } from "react";
import { Target, TrendingUp, Percent, Lightbulb } from "lucide-react";

const formatCurrency = (value: number) => {
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  }
  return `$${(value / 1e6).toFixed(0)}M`;
};

export function ValuationComps() {
  const [sector, setSector] = useState<string>("SaaS");
  const [revenue, setRevenue] = useState<number>(50000000);
  const [growthRate, setGrowthRate] = useState<number>(45); // YoY Growth %
  const [ebitdaMargin, setEbitdaMargin] = useState<number>(-15); // FCF / EBITDA Margin %
  const [grossMargin, setGrossMargin] = useState<number>(75); // %
  const [nrr, setNrr] = useState<number>(115); // Net Revenue Retention %

  // Sector Base Multiples (EV / NTM Revenue) - Highly illustrative market proxies
  const SECTOR_MULTIPLES: Record<string, number> = {
    SaaS: 8.0,
    AI: 14.5,
    Fintech: 6.5,
    Crypto: 9.0,
  };

  const valuationData = useMemo(() => {
    // 1. Calculate Rule of 40
    const ruleOf40 = growthRate + ebitdaMargin;

    // 2. Determine Base Multiple
    let calcMultiple = SECTOR_MULTIPLES[sector];

    let explanationStrings = [];
    explanationStrings.push(
      `Base ${sector} multiple: ${calcMultiple.toFixed(1)}x`,
    );

    // 3. Rule of 40 Premium/Discount
    if (ruleOf40 >= 60) {
      calcMultiple *= 1.4; // 40% premium for hyper-performers
      explanationStrings.push(`Rule of 40 > 60: Elite Premium applied (+40%)`);
    } else if (ruleOf40 >= 40) {
      calcMultiple *= 1.1; // 10% premium
      explanationStrings.push(`Rule of 40 Achieved: Premium applied (+10%)`);
    } else if (ruleOf40 < 20) {
      calcMultiple *= 0.7; // 30% penalty for sub-scale
      explanationStrings.push(
        `Rule of 40 < 20: Sub-scale Penalty applied (-30%)`,
      );
    }

    // 4. Gross Margin Adjustment
    if (grossMargin > 80) {
      calcMultiple *= 1.15;
      explanationStrings.push(
        `High Gross Margins (>80%): Premium applied (+15%)`,
      );
    } else if (grossMargin < 50) {
      calcMultiple *= 0.85;
      explanationStrings.push(
        `Low Gross Margins (<50%): Discount applied (-15%)`,
      );
    }

    // 5. NRR Adjustment (SaaS/AI primarily)
    if (nrr >= 125) {
      calcMultiple *= 1.15;
      explanationStrings.push(
        `Best-in-class NRR (=>125%): Premium applied (+15%)`,
      );
    }

    // 6. Calculate EV Ranges
    const baseEV = revenue * calcMultiple;
    const lowEV = baseEV * 0.85; // +/- 15% range for football field
    const highEV = baseEV * 1.15;

    const sectorBaseEV = revenue * SECTOR_MULTIPLES[sector];

    const premiumRatio = calcMultiple / SECTOR_MULTIPLES[sector] - 1;
    const premiumPercent = (Math.abs(premiumRatio) * 100).toFixed(0);
    const premiumText =
      premiumRatio > 0.01
        ? `a ${premiumPercent}% premium`
        : premiumRatio < -0.01
          ? `a ${premiumPercent}% discount`
          : "parity";
    const insightText = `Based on your Rule of 40 score (${ruleOf40}) and ${grossMargin}% Gross Margins, your target warrants a ${formatCurrency(baseEV)} valuation—${premiumText} compared to median public ${sector} companies.`;

    return {
      ruleOf40,
      multiple: calcMultiple,
      sectorMultiple: SECTOR_MULTIPLES[sector],
      lowEV,
      baseEV,
      highEV,
      sectorBaseEV,
      explanations: explanationStrings,
      insightText,
    };
  }, [sector, revenue, growthRate, ebitdaMargin, grossMargin, nrr]);

  // Data for the Football Field Chart
  const chartData = [
    {
      name: "Public Market Comps",
      Bottom: valuationData.sectorBaseEV * 0.85,
      Range:
        valuationData.sectorBaseEV * 1.15 - valuationData.sectorBaseEV * 0.85,
      Median: valuationData.sectorBaseEV,
    },
    {
      name: "Your Adjusted Target",
      Bottom: valuationData.lowEV,
      Range: valuationData.highEV - valuationData.lowEV,
      Median: valuationData.baseEV,
    },
    {
      name: "M&A / De-SPAC Premium",
      Bottom: valuationData.baseEV * 1.05,
      Range: valuationData.baseEV * 1.35 - valuationData.baseEV * 1.05,
      Median: valuationData.baseEV * 1.2,
    },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8">
      {/* Controls Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6 bg-card rounded-2xl border border-border shadow-sm">
        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Sector Benchmark
          </label>
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none"
          >
            <option value="SaaS">Enterprise B2B SaaS</option>
            <option value="AI">Applied Enterprise AI</option>
            <option value="Fintech">Financial Technology</option>
            <option value="Crypto">Crypto & Web3 Infra</option>
          </select>
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            NTM Revenue / ARR
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {formatCurrency(revenue)}
            </span>
          </label>
          <input
            type="range"
            min={10000000}
            max={250000000}
            step={5000000}
            value={revenue}
            onChange={(e) => setRevenue(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            YoY Revenue Growth
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {growthRate}%
            </span>
          </label>
          <input
            type="range"
            min={0}
            max={200}
            step={5}
            value={growthRate}
            onChange={(e) => setGrowthRate(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            FCF / EBITDA Margin
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {ebitdaMargin > 0 ? "+" : ""}
              {ebitdaMargin}%
            </span>
          </label>
          <input
            type="range"
            min={-100}
            max={50}
            step={5}
            value={ebitdaMargin}
            onChange={(e) => setEbitdaMargin(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Gross Margin
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {grossMargin}%
            </span>
          </label>
          <input
            type="range"
            min={20}
            max={95}
            step={1}
            value={grossMargin}
            onChange={(e) => setGrossMargin(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Net Revenue Retention (NRR)
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {nrr}%
            </span>
          </label>
          <input
            type="range"
            min={80}
            max={150}
            step={1}
            value={nrr}
            onChange={(e) => setNrr(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>
      </div>

      {/* Analytics Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cost Breakdown Cards */}
        <div className="lg:col-span-1 space-y-4">
          <div className="p-6 bg-card rounded-2xl border border-border shadow-sm h-full flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-6 border-b border-border pb-4">
                <div className="p-3 bg-primary/10 text-primary rounded-xl">
                  <Target size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Adjusted Target EV</h3>
                  <p className="text-sm text-muted-foreground">
                    Derived implied valuation
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <span className="block text-4xl font-mono text-foreground font-bold">
                  {formatCurrency(valuationData.baseEV)}
                </span>
                <span className="text-sm text-muted-foreground">
                  Implied EV/Rev Multiple:{" "}
                  <strong className="text-foreground">
                    {valuationData.multiple.toFixed(2)}x
                  </strong>
                </span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <TrendingUp size={16} /> Rule of 40 Score
                  </span>
                  <span
                    className={`font-mono font-bold px-2 py-0.5 rounded-md ${valuationData.ruleOf40 >= 40 ? "bg-emerald-500/20 text-emerald-500" : "bg-red-500/20 text-red-500"}`}
                  >
                    {valuationData.ruleOf40}
                  </span>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                  Algorithmic Adjustments
                </h4>
                <ul className="space-y-2 text-sm">
                  {valuationData.explanations.map((exp, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-1 flex-shrink-0 text-primary">
                        <Percent size={12} />
                      </span>
                      <span className="text-muted-foreground">{exp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="lg:col-span-2 p-6 bg-card rounded-2xl border border-border shadow-sm flex flex-col justify-center min-h-[400px]">
          <h3 className="text-xl font-bold mb-2 text-center">
            Valuation Football Field (EV)
          </h3>
          <p className="text-sm text-center text-muted-foreground mb-8">
            Visualizing the probable valuation range based on public market
            comparables.
          </p>

          <div className="w-full mt-4 h-full flex flex-col justify-center gap-10 font-sans relative px-4 pb-12 pt-6">
            {/* Base EV Reference Line */}
            <div
              className="absolute top-0 bottom-4 border-l-2 border-dashed border-foreground/50 z-0 transition-all duration-700 ease-in-out"
              style={{
                left: `${Math.min((valuationData.baseEV / Math.max(valuationData.highEV * 1.5, 500000000)) * 100, 100)}%`,
              }}
            >
              <div className="absolute -top-6 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider bg-background px-2 py-0.5 rounded-full border border-border shadow-sm text-foreground">
                Target
              </div>
            </div>

            {chartData.map((row, idx) => {
              const maxDomain = Math.max(valuationData.highEV * 1.5, 500000000);
              const leftPct = (row.Bottom / maxDomain) * 100;
              const widthPct = (row.Range / maxDomain) * 100;
              const medianPct = ((row.Median - row.Bottom) / row.Range) * 100;

              let gradientClass = "";
              if (idx === 0)
                gradientClass = "bg-gradient-to-r from-[#94a3b8] to-[#64748b]";
              if (idx === 1)
                gradientClass = "bg-gradient-to-r from-[#34d399] to-[#059669]";
              if (idx === 2)
                gradientClass = "bg-gradient-to-r from-[#fbbf24] to-[#d97706]";

              return (
                <div key={idx} className="flex items-center w-full z-10 group">
                  {/* Y Axis Label */}
                  <div className="w-40 text-xs font-semibold pr-4 text-foreground/80 leading-tight group-hover:text-foreground transition-colors">
                    {row.name}
                  </div>

                  {/* Track area */}
                  <div className="flex-1 relative h-10 border-b border-border/40 pb-2">
                    {/* The Bar */}
                    <div
                      className={`absolute top-0 bottom-2 rounded-md shadow-sm transition-all duration-700 ease-out flex items-center justify-center cursor-pointer hover:shadow-md hover:brightness-110 ${gradientClass}`}
                      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                    >
                      {/* Median Marker on the bar */}
                      <div
                        className="absolute top-0 bottom-0 w-[2px] bg-background/80"
                        style={{ left: `${medianPct}%` }}
                      ></div>
                    </div>

                    {/* Custom Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 translate-y-2 transition-all duration-300 pointer-events-none bg-card border border-border shadow-2xl rounded-xl p-4 z-50 w-56">
                      <p className="text-sm font-bold text-foreground mb-3 border-b border-border pb-2">
                        {row.name}
                      </p>
                      <div className="space-y-2">
                        <p className="font-mono text-sm flex justify-between">
                          <span className="text-muted-foreground font-sans text-xs">
                            High:
                          </span>
                          <span>{formatCurrency(row.Bottom + row.Range)}</span>
                        </p>
                        <p className="font-mono text-primary font-bold flex justify-between items-center py-1">
                          <span className="text-foreground font-sans text-[10px] uppercase tracking-wider">
                            Median:
                          </span>
                          <span className="text-sm">
                            {formatCurrency(row.Median)}
                          </span>
                        </p>
                        <p className="font-mono text-sm flex justify-between">
                          <span className="text-muted-foreground font-sans text-xs">
                            Low:
                          </span>
                          <span>{formatCurrency(row.Bottom)}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center text-xs text-muted-foreground mt-6">
            The dotted line represents your Adjusted Target EV against different
            market methodologies.
          </div>
        </div>
      </div>

      {/* Dynamic Data Insight */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 flex gap-4 items-start shadow-sm mt-8">
        <div className="p-3 bg-primary/10 rounded-full text-primary mt-1 flex-shrink-0">
          <Lightbulb size={24} />
        </div>
        <div>
          <h4 className="font-bold text-foreground mb-1 text-lg">
            Key Takeaway
          </h4>
          <p className="text-muted-foreground text-lg text-balance leading-relaxed">
            {valuationData.insightText}
          </p>
        </div>
      </div>
      {/* Disclaimer */}
      <div className="mt-12 text-xs text-muted-foreground/80 leading-relaxed max-w-4xl mx-auto text-center border-t border-border/50 pt-8">
        <p>
          <strong>Disclaimer:</strong> This tool is for informational and
          educational purposes only and does not constitute financial,
          investment, legal, or tax advice. The outputs generated are estimates
          based on specific assumptions and mathematical models. Actual
          transaction structures, dilution effects, and financial returns may
          vary significantly. Please consult with appropriate professional
          advisors before making any business decisions.
        </p>
      </div>
    </div>
  );
}
